import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import * as translations from 'constants/translations';
import { context as localeContext } from 'context/locale';
import { Row, Label } from 'components/form/generic';
import { Button } from 'components/kit';
import { Field, Text } from 'components/service';
import { RadioGroup, Input } from 'components/form/elements';
import { context as notificationsContext } from 'context/notifications';
import { useUpdateBulkDeliveryZone } from 'service/hooks';
import { useSelectedStore } from 'hooks';
import bulkValidationSchema from '../../BulkEdit/data';
import { bulkEditZones } from '../../utils';

const UpdateDeliveryFeesModal = ({ bulkZones, close, setZones }) => {
  const selectedStoreId = useSelectedStore();
  const notifications = useContext(notificationsContext);
  const { direction, translate } = useContext(localeContext);
  const initialValues = {
    needsDeliveryFee: true,
    deliveryFee: '',
  };
  const [pending, setPending] = useState(false);
  const [enableDeliveryFee, setEnableDeliveryFee] = useState(true);

  const updateBulkDeliveryZones = useUpdateBulkDeliveryZone();

  const handleFormSubmit = async values => {
    setPending(true);

    const response = await updateBulkDeliveryZones({
      deliveryZones: bulkZones.map(bulkItem => ({
        restaurantReferenceId: parseFloat(selectedStoreId),
        id: bulkItem.properties.id,
        deliveryFee: parseFloat(values.deliveryFee),
      })),
    });

    if (!response.hasError) {
      setPending(false);
      if (response.updateBulkDeliveryZones) {
        const { deliveryZone, errors } = response.updateBulkDeliveryZones;

        // - All zones failed
        if (deliveryZone.length === 0 && errors.length >= 1) {
          notifications.show(<p>{errors[0].errorMessage}</p>, 'error');
          close();
        }

        // - Some zones success and some failed
        if (deliveryZone.length >= 1 && errors.length >= 1) {
          notifications.show(
            <div className="flex flex-wrap">
              <p>{translate(translations.BULK_EDIT_ISSUE)} &nbsp;</p>
              <ul className="list-none flex flex-wrap">
                {errors.map(errorItem => (
                  <li key={errorItem.zoneName}>{errorItem.zoneName},&nbsp;</li>
                ))}
              </ul>
            </div>,
            'error',
          );
          bulkEditZones(setZones, deliveryZone, 'delivery_fee', parseFloat(values.deliveryFee));
          close();
        }

        // - All zones success
        if (deliveryZone.length >= 1 && errors.length === 0) {
          notifications.show(<Text value={translations.BULK_EDIT_SUCCESS_MSG(deliveryZone.length)} />);
          bulkEditZones(setZones, deliveryZone, 'delivery_fee', parseFloat(values.deliveryFee));
          close();
        }
      }
    } else {
      setPending(false);
      close();
      notifications.show(<Text value={translations.SOMETHING_WENT_WRONG} />, 'error');
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => handleFormSubmit(values)}
      validationSchema={bulkValidationSchema}
    >
      {({ setFieldValue }) => (
        <Form>
          <Row className="mt-2 mx-3">
            <RadioGroup
              items={[
                {
                  title: <Text value={translations.APPLY_DELIVERY_FEE} />,
                  value: true,
                },
                {
                  title: <Text value={translations.REMOVE_DELIVERY_FEE} />,
                  value: false,
                },
              ]}
              name="enable_delivery_fee"
              value={enableDeliveryFee}
              onChange={option => {
                const { value } = option.target;
                setEnableDeliveryFee(value);
                if (!value) {
                  setFieldValue('deliveryFee', 0);
                }
              }}
            />
          </Row>
          {enableDeliveryFee && (
            <Row className="mt-3">
              <Label textSize="text-xs" title={<Text value={translations.DELIVERY_FEES} />}>
                <Field
                  type="text"
                  name="deliveryFee"
                  component={Input}
                  placeholder={translate(translations.DELIVERY_FEES_PLACEHOLDER)}
                  direction={direction}
                />
              </Label>
            </Row>
          )}

          <div className="flex items-center justify-end mt-4">
            <Button onClick={close} type="button" kind="tertiary" className="mx-4">
              <Text value={translations.DISMISS} />
            </Button>
            <Button kind="primary" type="submit" disabled={pending}>
              <Text value={translations.CONFIRM} />
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

UpdateDeliveryFeesModal.propTypes = {
  bulkZones: PropTypes.arrayOf(
    PropTypes.shape({
      properties: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
  ),
  close: PropTypes.func.isRequired,
  setZones: PropTypes.func.isRequired,
};

export default UpdateDeliveryFeesModal;
